import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue";
const _hoisted_1 = {
    "data-testid": "subscriptions-table",
    class: "table-shell-no-clip flex flex-col flex-1 min-h-0"
};
const _hoisted_2 = { class: "relative h-full" };
const _hoisted_3 = {
    key: 0,
    class: "absolute inset-0 z-20 flex items-center justify-center bg-white/60 dark:bg-gray-900/60"
};
import { computed, h, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RecurringPaymentRuleStatus, RecurringPaymentFrequency, } from "@/domain/entities/RecurringPaymentRule";
import { useSubscriptionUtils } from "@/shared/composables/useSubscriptionUtils";
import { getDateFormatForLocale, displayDateToIso } from "@/shared/utils/dateFormat";
import { TableShell, DataTable, Pager, Badge, Dropdown, useTableQuery, useColumnSettings, TableFilterSelector, FROZEN_COLUMN_LIMIT, } from "@eudonet/opal";
import { logger } from "@/infrastructure/services/LoggerService";
const FALLBACK_AMOUNT_MAX = 10000;
const FALLBACK_TRANSACTION_COUNT_MAX = 100;
export default /*@__PURE__*/ _defineComponent({
    __name: 'SubscriptionsTable',
    props: {
        listItems: {},
        isLoading: { type: Boolean },
        hasItems: { type: Boolean },
        isSearchActive: { type: Boolean },
        currentPage: {},
        pageSize: {},
        totalCount: {},
        filterCounts: {}
    },
    emits: ["view-details", "update", "pause", "resume", "cancel-next", "cancel", "go-to-page", "page-size-change", "filters-change"],
    setup(__props, { emit: __emit }) {
        const { t, locale } = useI18n();
        const { renderFrequency, formatCurrency, formatDate, getStatusConfig, getStatusIcon, getOpalBadgeTheme, shouldShowNextTransactionDate, } = useSubscriptionUtils();
        // UUID v4 string format (8-4-4-4-12 hex). Used to validate the "Subscription ID" filter input
        // before sending it to the API — non-UUID input would otherwise be silently dropped server-side.
        const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const props = __props;
        const emit = __emit;
        // ── Component refs ────────────────────────────────────────────────────────
        const tableShellRef = ref(null);
        const filterSelectorRef = ref(null);
        // ── Column settings (visibility + pinning + order) via composable ────────
        const columnSettingsColumns = computed(() => [
            {
                key: "id",
                label: t("dashboard.subscriptions.table.subscriptionId"),
                visible: true,
                pinned: true,
                disabled: true,
                alwaysVisible: true,
            },
            {
                key: "merchantReference",
                label: t("dashboard.subscriptions.table.merchantReference"),
                visible: true,
                pinned: false,
            },
            {
                key: "firstTotalAmount",
                label: t("dashboard.subscriptions.table.firstTotalAmount", "First Total"),
                visible: true,
                pinned: false,
            },
            {
                key: "totalAmount",
                label: t("dashboard.subscriptions.table.totalAmount"),
                visible: true,
                pinned: false,
            },
            {
                key: "frequency",
                label: t("dashboard.subscriptions.table.frequency"),
                visible: true,
                pinned: false,
            },
            {
                key: "transactionCount",
                label: t("dashboard.subscriptions.table.numberOfTransactions"),
                visible: true,
                pinned: false,
            },
            { key: "status", label: t("dashboard.subscriptions.table.status"), visible: true, pinned: false },
            {
                key: "nextTransactionDate",
                label: t("dashboard.subscriptions.table.nextTransaction"),
                visible: true,
                pinned: false,
            },
        ]);
        const columnSettings = useColumnSettings({
            columns: columnSettingsColumns,
            trailingColumns: ["actions"],
            labels: computed(() => ({
                title: t("dashboard.subscriptions.table.columnSettings", "Column settings"),
                subtitle: (n, total) => t("dashboard.subscriptions.table.columnSettingsSubtitle", { n, total }, `${n} of ${total} selected`),
                searchPlaceholder: t("dashboard.subscriptions.table.columnSearchPlaceholder", "Search a column"),
                visibleTab: t("dashboard.subscriptions.table.visibleTab", "Visible"),
                orderTab: t("dashboard.subscriptions.table.orderTab", "Order"),
                defaultColumnHeader: t("dashboard.subscriptions.table.defaultColumnHeader", "Default fixed column"),
                pinnedBadge: t("dashboard.subscriptions.table.pinnedBadge", "Pinned"),
                resetLabel: t("dashboard.subscriptions.table.resetColumns", "Reset columns"),
                cancelLabel: t("dashboard.subscriptions.table.cancel", "Cancel"),
                saveLabel: t("dashboard.subscriptions.table.save", "Save"),
                closeAriaLabel: t("dashboard.subscriptions.table.closeAriaLabel", "Close"),
                emptySearchText: t("dashboard.subscriptions.table.emptySearchText", "No columns found"),
                emptyOrderText: t("dashboard.subscriptions.table.emptyOrderText", "No visible columns"),
                orderSearchHint: t("dashboard.subscriptions.table.orderSearchHint", "Showing filtered results. Drag to reorder within visible columns."),
                pinTooltip: t("dashboard.subscriptions.table.pinTooltip", "Locked column"),
                pinAriaLabelPinned: t("dashboard.subscriptions.table.pinAriaLabelPinned", "Unfreeze column"),
                pinAriaLabelUnpinned: t("dashboard.subscriptions.table.pinAriaLabelUnpinned", "Freeze column"),
                pinLimitTooltip: t("dashboard.subscriptions.table.pinLimitTooltip", `You can freeze up to ${FROZEN_COLUMN_LIMIT} columns. Unfreeze a column to freeze another.`),
                reorderMoved: (label, position, total) => t("dashboard.subscriptions.table.reorderMoved", { label, position, total }, `${label} moved to position ${position} of ${total}`),
                reorderPickedUp: (label) => t("dashboard.subscriptions.table.reorderPickedUp", { label }, `${label} picked up. Use arrow keys to move, space to drop, escape to cancel.`),
                reorderCancelled: (label) => t("dashboard.subscriptions.table.reorderCancelled", { label }, `${label} reorder cancelled`),
                orderListLabel: t("dashboard.subscriptions.table.orderListLabel", "Column order"),
            })),
        });
        // Amount range bounds shared by the filter selector panel and the pill inline editor.
        // Backend returns counts in minor units (cents); UI displays major units.
        // Slider min/max are wired to the actual data range so dragging stays within
        // meaningful values. Number inputs are decoupled (clampInputToBounds: false) —
        // typing a value beyond the data range is allowed; the slider thumb clamps to
        // the data max visually.
        function buildAmountRange(dataMinCents, dataMaxCents) {
            const dataMin = dataMinCents != null ? Math.floor(dataMinCents / 100) : null;
            const dataMax = dataMaxCents != null ? Math.ceil(dataMaxCents / 100) : null;
            // Guard against single-value datasets where dataMin === dataMax — the dual-thumb slider would lock.
            // Expand sliderMax by 1 so thumbs remain draggable; the hint label still surfaces the real bounds.
            const sliderMin = dataMin ?? 0;
            let sliderMax = dataMax ?? FALLBACK_AMOUNT_MAX;
            if (dataMin != null && dataMax != null && dataMin === dataMax) {
                sliderMax = dataMax + 1;
            }
            return {
                sliderMin,
                sliderMax,
                dataMin,
                dataMax,
            };
        }
        function buildTransactionCountRange(dataMin, dataMax) {
            const sliderMin = dataMin ?? 0;
            let sliderMax = dataMax ?? FALLBACK_TRANSACTION_COUNT_MAX;
            if (dataMin != null && dataMax != null && dataMin === dataMax) {
                sliderMax = dataMax + 1;
            }
            return {
                sliderMin,
                sliderMax,
                dataMin: dataMin ?? null,
                dataMax: dataMax ?? null,
            };
        }
        const transactionCountRange = computed(() => buildTransactionCountRange(props.filterCounts?.transactionCountMin, props.filterCounts?.transactionCountMax));
        const amountRange = computed(() => buildAmountRange(props.filterCounts?.amountMin, props.filterCounts?.amountMax));
        const firstAmountRange = computed(() => buildAmountRange(props.filterCounts?.firstAmountMin, props.filterCounts?.firstAmountMax));
        function formatAmountHint(range) {
            if (range.dataMin == null || range.dataMax == null)
                return "";
            return t("dashboard.subscriptions.filter.amountHint", { min: range.dataMin, max: range.dataMax }, `Available data range: ${range.dataMin} – ${range.dataMax}`);
        }
        // ── TableShell query state ─────────────────────────────────────────────────
        const tableQuery = useTableQuery({
            pageSize: props.pageSize,
            filterLabels: computed(() => ({
                status: t("dashboard.subscriptions.table.status"),
                frequency: t("dashboard.subscriptions.table.frequency"),
                amount: t("dashboard.subscriptions.table.totalAmount"),
                firstTotalAmount: t("dashboard.subscriptions.table.firstTotalAmount", "First Total"),
                nextTransactionDate: t("dashboard.subscriptions.table.nextTransaction"),
                subscriptionId: t("dashboard.subscriptions.table.subscriptionId"),
                merchantReference: t("dashboard.subscriptions.table.merchantReference"),
            })),
            filterItems: computed(() => ({
                status: [
                    RecurringPaymentRuleStatus.Active,
                    RecurringPaymentRuleStatus.Paused,
                    RecurringPaymentRuleStatus.Completed,
                    RecurringPaymentRuleStatus.Failed,
                    RecurringPaymentRuleStatus.Cancelled,
                ].map((s) => ({
                    id: s,
                    label: t(getStatusConfig(s).textKey),
                })),
                frequency: Object.values(RecurringPaymentFrequency).map((f) => ({
                    id: f,
                    label: renderFrequency(f),
                })),
            })),
            filterFieldConfigs: computed(() => ({
                subscriptionId: {
                    placeholder: t("dashboard.subscriptions.filter.subscriptionIdPlaceholder", "e.g. b6479b6b-1e7d-456b-861b-76539ea8bc6b"),
                },
                merchantReference: {
                    placeholder: t("dashboard.subscriptions.filter.merchantReferencePlaceholder", "e.g. b6479b6b-1e7d-456b-861b-76539ea8bc6b"),
                },
                amount: {
                    rangeConfig: {
                        min: amountRange.value.sliderMin,
                        max: amountRange.value.sliderMax,
                        step: 1,
                        separatorLabel: t("dashboard.subscriptions.filter.to", "to"),
                        clampInputToBounds: false,
                        hintLabel: formatAmountHint(amountRange.value),
                    },
                },
                firstTotalAmount: {
                    rangeConfig: {
                        min: firstAmountRange.value.sliderMin,
                        max: firstAmountRange.value.sliderMax,
                        step: 1,
                        separatorLabel: t("dashboard.subscriptions.filter.to", "to"),
                        clampInputToBounds: false,
                        hintLabel: formatAmountHint(firstAmountRange.value),
                    },
                },
                transactionCount: {
                    rangeConfig: {
                        min: transactionCountRange.value.sliderMin,
                        max: transactionCountRange.value.sliderMax,
                        step: 1,
                        separatorLabel: t("dashboard.subscriptions.filter.to", "to"),
                        clampInputToBounds: false,
                        hintLabel: transactionCountRange.value.dataMin != null && transactionCountRange.value.dataMax != null
                            ? t("dashboard.subscriptions.filter.transactionCountHint", {
                                min: transactionCountRange.value.dataMin,
                                max: transactionCountRange.value.dataMax,
                            })
                            : "",
                    },
                },
                nextTransactionDate: {
                    dateRangeConfig: {
                        format: getDateFormatForLocale(locale.value),
                        placeholder: getDateFormatForLocale(locale.value),
                        fromLabel: t("dashboard.subscriptions.filter.from", "From"),
                        toLabel: t("dashboard.subscriptions.filter.to", "To"),
                    },
                },
            })),
        });
        const totalPages = computed(() => props.totalCount > 0 ? Math.ceil(props.totalCount / props.pageSize) : 1);
        const tableLabels = computed(() => ({
            filterButton: t("dashboard.subscriptions.filter.button", "Filter"),
            eraseAll: t("dashboard.subscriptions.filter.clear", "Clear"),
            hideFilters: (n) => t("dashboard.subscriptions.filter.hideFilters", { count: n }, `Hide filters (${n})`),
            showFilters: (n) => t("dashboard.subscriptions.filter.showFilters", { count: n }, `Show filters (${n})`),
            closeSearch: t("dashboard.subscriptions.filter.closeSearch", "Close search"),
            removeFilter: (label) => t("dashboard.subscriptions.filter.removeFilter", { label }, `Remove ${label} filter`),
        }));
        // ── Filter fields for TableFilterSelector (all 6 types) ───────────────────
        const filterFields = computed(() => [
            {
                key: "status",
                label: t("dashboard.subscriptions.table.status"),
                icon: "ri-pulse-line",
                type: "list",
                items: [
                    RecurringPaymentRuleStatus.Active,
                    RecurringPaymentRuleStatus.Paused,
                    RecurringPaymentRuleStatus.Completed,
                    RecurringPaymentRuleStatus.Failed,
                    RecurringPaymentRuleStatus.Cancelled,
                ].map((s) => ({
                    id: s,
                    label: t(getStatusConfig(s).textKey),
                    count: props.filterCounts?.statusCounts[s],
                })),
            },
            {
                key: "frequency",
                label: t("dashboard.subscriptions.table.frequency"),
                icon: "ri-repeat-line",
                type: "list",
                items: Object.values(RecurringPaymentFrequency).map((f) => ({
                    id: f,
                    label: renderFrequency(f),
                    count: props.filterCounts?.frequencyCounts[f],
                })),
            },
            {
                key: "subscriptionId",
                label: t("dashboard.subscriptions.table.subscriptionId"),
                icon: "ri-hashtag",
                type: "text",
                placeholder: t("dashboard.subscriptions.filter.subscriptionIdPlaceholder", "e.g. b6479b6b-1e7d-456b-861b-76539ea8bc6b"),
                maxLength: 36, // UUID format: 8-4-4-4-12
                maxLengthErrorLabel: t("dashboard.subscriptions.filter.maxLengthError", { max: 36 }, "Maximum 36 characters"),
                // Backend rejects non-UUID input — validate client-side so users see an inline error instead of
                // a silently-dropped filter (R29 + DS rule).
                validate: (value) => UUID_REGEX.test(value)
                    ? null
                    : t("dashboard.subscriptions.filter.subscriptionIdInvalidFormat", "Invalid subscription ID format."),
            },
            {
                key: "merchantReference",
                label: t("dashboard.subscriptions.table.merchantReference"),
                icon: "ri-store-line",
                type: "text",
                placeholder: t("dashboard.subscriptions.filter.merchantReferencePlaceholder", "e.g. b6479b6b-1e7d-456b-861b-76539ea8bc6b"),
                maxLength: 43, // MerchantReferenceHelper.MaxInternalReferenceLength (AdyenMaxRef 80 - GuidSuffix 37)
                maxLengthErrorLabel: t("dashboard.subscriptions.filter.maxLengthError", { max: 43 }, "Maximum 43 characters"),
            },
            {
                key: "amount",
                label: t("dashboard.subscriptions.table.totalAmount"),
                icon: "ri-money-euro-circle-line",
                type: "range",
                rangeMin: amountRange.value.sliderMin,
                rangeMax: amountRange.value.sliderMax,
                rangeStep: 1,
                rangeSeparatorLabel: t("dashboard.subscriptions.filter.to", "to"),
                rangeErrorLabel: t("dashboard.subscriptions.filter.rangeError", "Min value must be less than or equal to max value"),
                rangeClampInputToBounds: false,
                rangeHintLabel: formatAmountHint(amountRange.value),
            },
            {
                key: "firstTotalAmount",
                label: t("dashboard.subscriptions.table.firstTotalAmount", "First Total"),
                icon: "ri-money-euro-circle-line",
                type: "range",
                rangeMin: firstAmountRange.value.sliderMin,
                rangeMax: firstAmountRange.value.sliderMax,
                rangeStep: 1,
                rangeSeparatorLabel: t("dashboard.subscriptions.filter.to", "to"),
                rangeErrorLabel: t("dashboard.subscriptions.filter.rangeError", "Min value must be less than or equal to max value"),
                rangeClampInputToBounds: false,
                rangeHintLabel: formatAmountHint(firstAmountRange.value),
            },
            {
                key: "transactionCount",
                label: t("dashboard.subscriptions.table.numberOfTransactions"),
                icon: "ri-repeat-2-line",
                type: "range",
                rangeMin: transactionCountRange.value.sliderMin,
                rangeMax: transactionCountRange.value.sliderMax,
                rangeStep: 1,
                rangeSeparatorLabel: t("dashboard.subscriptions.filter.to", "to"),
                rangeErrorLabel: t("dashboard.subscriptions.filter.rangeError", "Min value must be less than or equal to max value"),
                rangeClampInputToBounds: false,
                rangeHintLabel: transactionCountRange.value.dataMin != null && transactionCountRange.value.dataMax != null
                    ? t("dashboard.subscriptions.filter.transactionCountHint", {
                        min: transactionCountRange.value.dataMin,
                        max: transactionCountRange.value.dataMax,
                    })
                    : "",
            },
            {
                key: "nextTransactionDate",
                label: t("dashboard.subscriptions.table.nextTransaction"),
                icon: "ri-calendar-line",
                type: "dateRange",
                dateFormat: getDateFormatForLocale(locale.value),
                datePlaceholder: getDateFormatForLocale(locale.value),
                dateFromLabel: t("dashboard.subscriptions.filter.from", "From"),
                dateToLabel: t("dashboard.subscriptions.filter.to", "To"),
            },
        ]);
        function handleFilterApply(key, values) {
            if (values.length > 0) {
                tableQuery.setFilter(key, { type: "in", values });
            }
            else {
                tableQuery.removeFilter(key);
            }
        }
        function handleTypedFilterApply(key, value) {
            tableQuery.setFilter(key, value);
        }
        // ── Sorting helpers ────────────────────────────────────────────────────────
        function makeTextSortItems(columnKey) {
            return [
                {
                    label: t("dashboard.subscriptions.table.sortTextAsc", "Sort A → Z"),
                    onClick: () => tableQuery.setSort(columnKey, "asc"),
                },
                {
                    label: t("dashboard.subscriptions.table.sortTextDesc", "Sort Z → A"),
                    onClick: () => tableQuery.setSort(columnKey, "desc"),
                },
            ];
        }
        function makeNumericSortItems(columnKey) {
            return [
                {
                    label: t("dashboard.subscriptions.table.sortNumAsc", "Sort 0 → 9"),
                    onClick: () => tableQuery.setSort(columnKey, "asc"),
                },
                {
                    label: t("dashboard.subscriptions.table.sortNumDesc", "Sort 9 → 0"),
                    onClick: () => tableQuery.setSort(columnKey, "desc"),
                },
            ];
        }
        function makeDateSortItems(columnKey) {
            return [
                {
                    label: t("dashboard.subscriptions.table.sortDateAsc", "Sort oldest → newest"),
                    onClick: () => tableQuery.setSort(columnKey, "asc"),
                },
                {
                    label: t("dashboard.subscriptions.table.sortDateDesc", "Sort newest → oldest"),
                    onClick: () => tableQuery.setSort(columnKey, "desc"),
                },
            ];
        }
        // Mapping column keys to filter field keys (only columns with corresponding filters)
        const columnToFilterKey = {
            id: "subscriptionId",
            merchantReference: "merchantReference",
            totalAmount: "amount",
            firstTotalAmount: "firstTotalAmount",
            frequency: "frequency",
            transactionCount: "transactionCount",
            status: "status",
            nextTransactionDate: "nextTransactionDate",
        };
        function onTablePinColumn(columnId, position) {
            if (position === "left")
                columnSettings.pinColumn(columnId);
            else
                columnSettings.unpinColumn(columnId);
        }
        function makeFilterItem(columnKey) {
            const filterKey = columnToFilterKey[columnKey];
            if (!filterKey)
                return null;
            return {
                label: t("dashboard.subscriptions.table.filter", "Filter"),
                icon: "ri-filter-line",
                dividerBefore: true,
                onClick: () => filterSelectorRef.value?.openForField(filterKey),
            };
        }
        function makePinItem(columnKey) {
            const pinned = columnSettings.isPinned(columnKey);
            // DS rule (Notion + Figma): same pin icon family as the drawer — filled when pinned, outlined when not.
            // Mirrors the drawer's disabled pin button: refusing at the cap must be visible, not silent.
            const disabled = !pinned && !columnSettings.canPinColumn(columnKey);
            return {
                label: pinned
                    ? t("dashboard.subscriptions.table.unpinColumn", "Unpin column")
                    : t("dashboard.subscriptions.table.pinColumn", "Pin column"),
                icon: pinned ? "ri-pushpin-2-fill" : "ri-pushpin-2-line",
                disabled,
                onClick: () => {
                    if (pinned) {
                        columnSettings.unpinColumn(columnKey);
                    }
                    else {
                        columnSettings.pinColumn(columnKey);
                    }
                },
            };
        }
        // ── Bridge: tableQuery filters + sort → parent API call ───────────────────
        watch([() => tableQuery.state.filters, () => tableQuery.state.sort], ([filters, sort]) => {
            const converted = {};
            const statusFilter = filters["status"];
            if (statusFilter?.type === "in") {
                converted.statuses = statusFilter.values;
            }
            const frequencyFilter = filters["frequency"];
            if (frequencyFilter?.type === "in") {
                converted.frequencies = frequencyFilter.values;
            }
            // Amount: stored in user currency, convert to cents for API
            const amountFilter = filters["amount"];
            if (amountFilter?.type === "range") {
                if (amountFilter.min != null)
                    converted.amountMin = Math.round(amountFilter.min * 100);
                if (amountFilter.max != null)
                    converted.amountMax = Math.round(amountFilter.max * 100);
            }
            const firstAmountFilter = filters["firstTotalAmount"];
            if (firstAmountFilter?.type === "range") {
                if (firstAmountFilter.min != null)
                    converted.firstAmountMin = Math.round(firstAmountFilter.min * 100);
                if (firstAmountFilter.max != null)
                    converted.firstAmountMax = Math.round(firstAmountFilter.max * 100);
            }
            // Transaction count: integer range, no unit conversion
            const transactionCountFilter = filters["transactionCount"];
            if (transactionCountFilter?.type === "range") {
                if (transactionCountFilter.min != null)
                    converted.transactionCountMin = Math.round(transactionCountFilter.min);
                if (transactionCountFilter.max != null)
                    converted.transactionCountMax = Math.round(transactionCountFilter.max);
            }
            // Date: convert from locale display format to ISO 8601 for the API
            const dateFilter = filters["nextTransactionDate"];
            if (dateFilter?.type === "dateRange") {
                const fmt = getDateFormatForLocale(locale.value);
                if (dateFilter.from)
                    converted.dateFrom = displayDateToIso(dateFilter.from, fmt);
                if (dateFilter.to)
                    converted.dateTo = displayDateToIso(dateFilter.to, fmt);
            }
            const subscriptionIdFilter = filters["subscriptionId"];
            if (subscriptionIdFilter?.type === "equals") {
                converted.subscriptionId = String(subscriptionIdFilter.value);
            }
            const merchantRefFilter = filters["merchantReference"];
            if (merchantRefFilter?.type === "equals") {
                converted.merchantReference = String(merchantRefFilter.value);
            }
            // Sort
            if (sort) {
                converted.sortBy = sort.column;
                converted.sortOrder = sort.direction === "asc" ? "ASC" : "DESC";
            }
            emit("filters-change", converted);
        }, { deep: true });
        const emptyText = computed(() => tableQuery.hasActiveFilters.value
            ? t("dashboard.subscriptions.search.noResults", "No subscriptions found")
            : t("dashboard.subscriptions.empty.message"));
        // ── Copy-to-clipboard ──────────────────────────────────────────────────────
        const copiedValue = ref(null);
        async function copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
                copiedValue.value = text;
                setTimeout(() => {
                    copiedValue.value = null;
                }, 2000);
            }
            catch (error) {
                logger.debug("Failed to copy to clipboard:", error);
            }
        }
        function renderCopyableCell(value, testIdPrefix, rowId) {
            if (!value)
                return "-";
            return h("div", { class: "flex items-center gap-1.5 group min-w-0 focus-within:opacity-100" }, [
                h("span", {
                    class: "truncate",
                    "data-testid": `${testIdPrefix}-${rowId}`,
                }, value),
                h("button", {
                    class: "shrink-0 min-w-6 min-h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-(--colors-icon-base) hover:text-(--colors-text-heading) dark:hover:text-(--colors-text-white-default) cursor-pointer",
                    title: t("dashboard.subscriptions.detail.copyToClipboard"),
                    "aria-label": t("dashboard.subscriptions.detail.copyToClipboard"),
                    onClick: (e) => {
                        e.stopPropagation();
                        copyToClipboard(value);
                    },
                }, [
                    copiedValue.value === value
                        ? h("i", { class: "ri-check-line text-(--colors-icon-success) text-sm" })
                        : h("i", { class: "ri-file-copy-line text-sm" }),
                ]),
            ]);
        }
        // ── Status helpers ─────────────────────────────────────────────────────────
        function canEdit(status) {
            return (status === RecurringPaymentRuleStatus.Active || status === RecurringPaymentRuleStatus.Paused);
        }
        function canPause(status) {
            return status === RecurringPaymentRuleStatus.Active;
        }
        function canResume(status) {
            return status === RecurringPaymentRuleStatus.Paused;
        }
        function canCancelNext(status) {
            return (status === RecurringPaymentRuleStatus.Active || status === RecurringPaymentRuleStatus.Paused);
        }
        function canCancel(status) {
            return (status !== RecurringPaymentRuleStatus.Cancelled &&
                status !== RecurringPaymentRuleStatus.Completed &&
                status !== RecurringPaymentRuleStatus.Failed);
        }
        function getDropdownActions(item) {
            const actions = [];
            actions.push({
                label: t("dashboard.subscriptions.actions.viewDetails", "View details"),
                testId: "action-view-details",
                onClick: () => emit("view-details", item),
            });
            if (canEdit(item.status)) {
                actions.push({
                    label: t("dashboard.subscriptions.actions.edit"),
                    testId: "action-edit",
                    onClick: () => emit("update", item),
                });
            }
            if (canPause(item.status)) {
                actions.push({
                    label: t("dashboard.subscriptions.actions.pause"),
                    testId: "action-pause",
                    onClick: () => emit("pause", item),
                });
            }
            if (canResume(item.status)) {
                actions.push({
                    label: t("dashboard.subscriptions.actions.resume"),
                    testId: "action-resume",
                    onClick: () => emit("resume", item),
                });
            }
            if (canCancelNext(item.status)) {
                actions.push({
                    label: t("dashboard.subscriptions.actions.cancelNext"),
                    testId: "action-cancel-next",
                    onClick: () => emit("cancel-next", item),
                });
            }
            if (canCancel(item.status)) {
                actions.push({
                    label: t("dashboard.subscriptions.actions.cancel"),
                    testId: "action-cancel",
                    onClick: () => emit("cancel", item),
                });
            }
            return actions;
        }
        // ── DataTable column definitions ───────────────────────────────────────────
        const columns = computed(() => [
            {
                key: "id",
                header: t("dashboard.subscriptions.table.subscriptionId"),
                cell: ({ row }) => renderCopyableCell(row.original.id, "subscription-row", row.original.id),
                // Locked column (alwaysVisible+disabled in column-settings): drag handle + pin icon render with
                // a disabled style; column never moves and cannot be unpinned. Sort + Filter only in the menu.
                draggable: true,
                enablePinning: true,
                locked: true,
                headerMenuItems: [...makeTextSortItems("id"), makeFilterItem("id")].filter((item) => item != null),
            },
            {
                key: "merchantReference",
                header: t("dashboard.subscriptions.table.merchantReference"),
                cell: ({ row }) => renderCopyableCell(row.original.merchantReference, "subscription-merchant", row.original.id),
                draggable: true,
                enablePinning: true,
                headerMenuItems: [
                    ...makeTextSortItems("merchantReference"),
                    makeFilterItem("merchantReference"),
                    makePinItem("merchantReference"),
                ].filter((item) => item != null),
            },
            {
                key: "firstTotalAmount",
                header: t("dashboard.subscriptions.table.firstTotalAmount", "First Total"),
                cell: ({ row }) => h("span", { "data-testid": `subscription-first-total-${row.original.id}` }, row.original.firstTotalAmount != null
                    ? formatCurrency(row.original.firstTotalAmount, row.original.currency)
                    : "-"),
                draggable: true,
                enablePinning: true,
                width: 120,
                minWidth: 100,
                headerMenuItems: [
                    ...makeNumericSortItems("firstTotalAmount"),
                    makeFilterItem("firstTotalAmount"),
                    makePinItem("firstTotalAmount"),
                ].filter((item) => item != null),
            },
            {
                key: "totalAmount",
                header: t("dashboard.subscriptions.table.totalAmount"),
                cell: ({ row }) => h("span", { "data-testid": `subscription-total-${row.original.id}` }, formatCurrency(row.original.totalAmount, row.original.currency)),
                draggable: true,
                enablePinning: true,
                width: 120,
                minWidth: 100,
                headerMenuItems: [
                    ...makeNumericSortItems("totalAmount"),
                    makeFilterItem("totalAmount"),
                    makePinItem("totalAmount"),
                ].filter((item) => item != null),
            },
            {
                key: "frequency",
                header: t("dashboard.subscriptions.table.frequency"),
                cell: ({ row }) => h("span", { "data-testid": `subscription-frequency-${row.original.id}` }, renderFrequency(row.original.frequency)),
                draggable: true,
                enablePinning: true,
                width: 120,
                minWidth: 100,
                headerMenuItems: [
                    ...makeTextSortItems("frequency"),
                    makeFilterItem("frequency"),
                    makePinItem("frequency"),
                ].filter((item) => item != null),
            },
            {
                key: "transactionCount",
                header: t("dashboard.subscriptions.table.numberOfTransactions"),
                cell: ({ row }) => {
                    const count = row.original.transactionCount;
                    if (count === null || count === undefined || count === 0)
                        return t("dashboard.subscriptions.table.unlimited");
                    return `${row.original.transactionsCompleted ?? 0} / ${count}`;
                },
                draggable: true,
                enablePinning: true,
                width: 140,
                minWidth: 100,
                headerMenuItems: [
                    ...makeNumericSortItems("transactionCount"),
                    makeFilterItem("transactionCount"),
                    makePinItem("transactionCount"),
                ].filter((item) => item != null),
            },
            {
                key: "status",
                header: t("dashboard.subscriptions.table.status"),
                cell: ({ row }) => h("span", { "data-testid": `subscription-status-${row.original.id}` }, [
                    h(Badge, {
                        label: t(getStatusConfig(row.original.status).textKey),
                        theme: getOpalBadgeTheme(row.original.status),
                        size: "sm",
                        showCloseIcon: false,
                        // DS rule (Notion + Figma 4337:41552): a Badge cannot use the dot variant without an icon.
                        // Use the default variant + a status-icon slot to convey state visually.
                        type: "default",
                        showLeftIcon: true,
                    }, { "left-icon": () => h("i", { class: `${getStatusIcon(row.original.status)} text-xs` }) }),
                ]),
                draggable: true,
                enablePinning: true,
                width: 140,
                minWidth: 100,
                headerMenuItems: [
                    ...makeTextSortItems("status"),
                    makeFilterItem("status"),
                    makePinItem("status"),
                ].filter((item) => item != null),
            },
            {
                key: "nextTransactionDate",
                header: t("dashboard.subscriptions.table.nextTransaction"),
                cell: ({ row }) => shouldShowNextTransactionDate(row.original.status)
                    ? formatDate(row.original.nextTransactionDate ?? null)
                    : "",
                draggable: true,
                enablePinning: true,
                width: 140,
                minWidth: 100,
                headerMenuItems: [
                    ...makeDateSortItems("nextTransactionDate"),
                    makeFilterItem("nextTransactionDate"),
                    makePinItem("nextTransactionDate"),
                ].filter((item) => item != null),
            },
            {
                id: "actions",
                header: t("dashboard.subscriptions.table.actions", "Actions"),
                cell: ({ row }) => h(Dropdown, {
                    label: "",
                    ariaLabel: t("dashboard.subscriptions.table.actions"),
                    triggerIconLeft: "ri-more-2-fill",
                    ghost: true,
                    items: getDropdownActions(row.original),
                    menuWidth: "220px",
                    menuAlign: "right",
                }),
                resizable: false,
                draggable: false,
                noTruncate: true,
                width: 100,
                minWidth: 100,
                maxWidth: 100,
            },
        ]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(TableShell), {
                    ref_key: "tableShellRef",
                    ref: tableShellRef,
                    query: _unref(tableQuery),
                    "column-settings": _unref(columnSettings),
                    "show-topbar": false,
                    "show-controls": true,
                    "show-search": false,
                    "show-view-selector": false,
                    labels: tableLabels.value,
                    permissions: { canSaveView: false, canShareView: false },
                    onEditFilter: _cache[3] || (_cache[3] = (key) => filterSelectorRef.value?.openForField(key))
                }, {
                    filter: _withCtx(() => [
                        _createVNode(_unref(TableFilterSelector), {
                            ref_key: "filterSelectorRef",
                            ref: filterSelectorRef,
                            fields: filterFields.value,
                            "active-filters": _unref(tableQuery).state.filters,
                            "trigger-label": _unref(t)('dashboard.subscriptions.filter.button', 'Filter'),
                            "trigger-icon-left": "ri-filter-2-line",
                            "step1-search-placeholder": _unref(t)('dashboard.subscriptions.filter.searchPlaceholder', 'Search a filter...'),
                            "confirm-label": _unref(t)('dashboard.subscriptions.filter.apply', 'Apply'),
                            "back-label": _unref(t)('dashboard.subscriptions.filter.back', '← Back'),
                            "advanced-filters-label": "",
                            "date-range-error-label": _unref(t)('dashboard.subscriptions.filter.dateRangeError', 'The "From" date must be before or equal to the "To" date.'),
                            "no-results-label": _unref(t)('dashboard.subscriptions.filter.noFiltersMatch', 'No results for'),
                            onApply: handleFilterApply,
                            onApplyFilter: handleTypedFilterApply,
                            onRemoveFilter: _cache[0] || (_cache[0] = (key) => _unref(tableQuery).removeFilter(key))
                        }, null, 8 /* PROPS */, ["fields", "active-filters", "trigger-label", "step1-search-placeholder", "confirm-label", "back-label", "date-range-error-label", "no-results-label"])
                    ]),
                    table: _withCtx(() => [
                        _createElementVNode("div", _hoisted_2, [
                            (__props.isLoading)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[4] || (_cache[4] = [
                                        _createElementVNode("i", { class: "ri-loader-4-line animate-spin text-2xl text-(--colors-text-body-subtle)" }, null, -1 /* CACHED */)
                                    ]))]))
                                : _createCommentVNode("v-if", true),
                            _createVNode(_unref(DataTable), {
                                columns: columns.value,
                                data: __props.listItems,
                                "empty-text": emptyText.value,
                                "has-settings": "",
                                "sticky-header": "",
                                "column-visibility": _unref(columnSettings).visibility,
                                "column-pinning": _unref(columnSettings).pinning.value,
                                "column-order": _unref(columnSettings).order.value,
                                "settings-aria-label": _unref(t)('dashboard.subscriptions.table.columnSettings', 'Column settings'),
                                onSettingsClick: _unref(columnSettings).openDrawer,
                                "onUpdate:columnOrder": _unref(columnSettings).setOrder,
                                "onUpdate:columnPinning": _unref(columnSettings).setPinning,
                                onPinColumn: onTablePinColumn
                            }, null, 8 /* PROPS */, ["columns", "data", "empty-text", "column-visibility", "column-pinning", "column-order", "settings-aria-label", "onSettingsClick", "onUpdate:columnOrder", "onUpdate:columnPinning"])
                        ])
                    ]),
                    pagination: _withCtx(() => [
                        _createVNode(_unref(Pager), {
                            "current-page": __props.currentPage,
                            "total-pages": totalPages.value,
                            "total-items": __props.totalCount,
                            "items-per-page": __props.pageSize,
                            "rows-per-page-options": [10, 30, 50],
                            "rows-per-page-label": _unref(t)('dashboard.subscriptions.pagination.rowsPerPage', 'Rows per page'),
                            "previous-label": _unref(t)('dashboard.subscriptions.pagination.previous', 'Previous'),
                            "next-label": _unref(t)('dashboard.subscriptions.pagination.next', 'Next'),
                            "items-count-separator": _unref(t)('dashboard.subscriptions.pagination.of', 'of'),
                            "go-to-page-label": _unref(t)('dashboard.subscriptions.pagination.goToPage', 'Go to page'),
                            "go-label": _unref(t)('dashboard.subscriptions.pagination.go', 'Go'),
                            disabled: __props.totalCount === 0,
                            "show-rows-per-page": true,
                            "show-items-count": false,
                            "show-go-to-page": "",
                            onPageChange: _cache[1] || (_cache[1] = (page) => emit('go-to-page', page)),
                            onItemsPerPageChange: _cache[2] || (_cache[2] = (size) => emit('page-size-change', size))
                        }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "previous-label", "next-label", "items-count-separator", "go-to-page-label", "go-label", "disabled"])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["query", "column-settings", "labels"])
            ]));
        };
    }
});
